import { Fragment, useCallback } from 'react'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import { AnswerID } from '@aytm/app/swidget/base/questions/types'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import { unescapeTags } from '@aytm/lib/common/func'
import { EcommerceBannerPlacement, IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { useFamilyAxes } from '../../hooks/useFamilyAxes.ts'
import { useBannerImpression } from '../../hooks/useBannerImpression.ts'
import { LEGACY_AXIS } from '../../utils/familyAxes.ts'
import AddToCartButton from './AddToCartButton.tsx'
import style from './ProductCard.module.scss'
import saveToListIcon from '../assets/icons/save-to-list.svg'
import stockIcon from '../assets/icons/stock-icon.svg'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'

interface IProductCardProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  amount: number
  findability: boolean
  compact?: boolean
  saveToList?: boolean
  disabled?: boolean
  registerCardRef?: (id: AnswerID, element: HTMLDivElement | null) => void
  bannerPlacement?: EcommerceBannerPlacement
  'data-testid'?: string
  onSelect: () => void
  onAdd: (qnt: number) => void
}

const ProductCard = ({
  schema,
  answer,
  amount,
  findability,
  compact = false,
  saveToList = true,
  disabled = false,
  registerCardRef,
  bannerPlacement,
  'data-testid': dataTestId,
  onSelect,
  onAdd
}: IProductCardProps) => {
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()
  const { axes, currentSelections } = useFamilyAxes({ schema, answer })
  const mobileRow = viewport.mobile && !compact
  const hasDiscount = Boolean(answer.discounted_price) && answer.price > answer.discounted_price
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  const setCardRef = useCallback(
    (element: HTMLDivElement | null) => registerCardRef?.(answer.id, element),
    [answer.id, registerCardRef]
  )

  // Called unconditionally per the Rules of Hooks; a null placement short-circuits inside it. The
  // two refs never meet: only the grid registers a card, and the grid is not a banner placement.
  const bannerRef = useBannerImpression(answer.id, bannerPlacement ?? null)
  const combinedRef = bannerPlacement ? bannerRef : setCardRef
  const leadValue = axes.length > 0 ? currentSelections[axes[0].name] : undefined
  const variantParts = axes
    .filter(axis => axis.name !== LEGACY_AXIS && axis.values.length > 1)
    .map(axis =>
      t<(count: number, axis: string) => string>(
        'questions',
        'ec',
        'kroger',
        'moreVariants'
      )(axis.values.length - 1, axis.name.toLowerCase())
    )

  return (
    <div
      ref={combinedRef}
      data-testid={dataTestId}
      aria-disabled={disabled}
      className={`${style.card} ${compact ? style.compact : ''} ${mobileRow ? style.mobile : ''} ${
        disabled ? style.disabled : ''
      }`}
    >
      {saveToList && (
        <div className={style.topRow}>
          <EmptyLink color="#1860ae">
            <div className={style.saveToList}>
              <img src={saveToListIcon} alt="" role="presentation" className={style.saveToListIcon} />
              {t('questions', 'ec', 'kroger', 'saveToList')}
            </div>
          </EmptyLink>
        </div>
      )}
      <div className={style.imageWrapper} onClick={onSelect}>
        {answer.sponsored && <span className={style.sponsored}>{t('questions', 'ec', 'kroger', 'sponsored')}</span>}
        {answer.image ? (
          <div
            className={style.image}
            role="img"
            aria-label={answer.image.alt_text || t('questions', 'img', 'noAlt')}
            style={{ backgroundImage: `url('${getMediumAssetPath(answer.image)}')` }}
          />
        ) : (
          <div className={style.noImage} />
        )}
      </div>

      <div className={style.info}>
        <div className={style.body} onClick={onSelect}>
          {answer.red_label && (
            <div className={style.redLabel} dangerouslySetInnerHTML={{ __html: answer.red_label }} />
          )}
          <div className={style.priceRow}>
            {hasDiscount ? (
              <>
                <span
                  className={style.priceSale}
                  dangerouslySetInnerHTML={{ __html: price(answer.discounted_price) }}
                />
                <span className={style.priceOld} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
              </>
            ) : (
              <span className={style.price} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
            )}
          </div>
          {answer.per_count_price && (
            <div
              className={style.unitPrice}
              dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }}
            />
          )}
          <div className={style.title} dangerouslySetInnerHTML={{ __html: answer.text }} />
          {(leadValue || variantParts.length > 0) && (
            <div className={style.meta}>
              {leadValue && <span className={style.size}>{leadValue}</span>}
              {variantParts.map((part, i) => (
                <Fragment key={part}>
                  {(leadValue || i > 0) && <span className={style.metaDivider}> | </span>}
                  <span className={style.variantMore}>{part}</span>
                </Fragment>
              ))}
            </div>
          )}
          {answer.amazon_choice && <div className={style.snapEbt}>{t('questions', 'ec', 'kroger', 'snapEbt')}</div>}
        </div>

        <div className={style.cta}>
          {answer.overal_pick && (
            <div className={style.lowStock}>
              <img src={stockIcon} alt="" role="presentation" className={style.lowStockIcon} />
              {t('questions', 'ec', 'kroger', 'lowStock')}
            </div>
          )}
          <AddToCartButton
            amount={amount}
            findability={findability}
            limit={schema.max_qty_per_product}
            disabled={disabled}
            spread={mobileRow}
            onAdd={onAdd}
          />
        </div>
      </div>
    </div>
  )
}

export default ProductCard
